import { useAtomValue } from 'jotai';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { navbarHeightAtom } from '../utils/AtomConfig';
import { AuthService } from '../utils/AuthService';
import Constants from '../utils/Constants';
import { SharePointService } from '../utils/SharePointService';

const LOGO_PATH = './logo-2.png';
const GOOGLE_LOGO_PATH = './google-logo.png';
const MICROSOFT_LOGO_PATH = './microsoft-logo.png';

const useStyles = makeStyles({
  sop: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    backgroundColor: '#e8ecf1',
    paddingBottom: '20px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  logo: {
    width: 100,
    margin: '20px 0px',
  },
  textContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  button: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
      height: 24,
    },
    span: {
      marginLeft: 10,
      marginTop: 5,
    },
  },
  securityNotice: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    fontSize: 12,
    lineHeight: 1.4,
    color: '#333',
    '& h4': {
      margin: '0 0 10px 0',
      fontSize: 14,
      fontWeight: 'bold',
      color: '#000',
    },
    '& p': {
      margin: '0 0 8px 0',
    },
    '& ul': {
      margin: '5px 0 10px 0',
      paddingLeft: 20,
    },
    '& li': {
      marginBottom: 3,
    },
  },
});

export function Login() {
  const classes = useStyles();
  const authService = AuthService.Instance;
  const sharePointService = SharePointService.Instance;

  const navbarHeight = useAtomValue(navbarHeightAtom);

  const { data: authSettings } = useQuery(
    Constants.QUERY_KEYS.AUTH_SETTINGS,
    () => sharePointService.getAuthSettings(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    }
  );

  const isSessionTimedOut = authService.checkIsSessionTimedOut();

  useEffect(() => {
    authService.removeExpirationTime();
  }, [authService]);

  return (
    <Box className={classes.sop} paddingTop={`${navbarHeight + 20}px`}>
      <Box sx={{ maxWidth: 600, width: '100%' }}>
        {isSessionTimedOut && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <div>
              You’ve been signed out due to inactivity for security reasons.
            </div>
            <div>Please sign in again to continue.</div>
          </Alert>
        )}
        <Card>
          <CardContent>
            <div className={classes.textContainer}>
              <img className={classes.logo} src={LOGO_PATH} alt='Logo'></img>
              <Typography
                sx={{ fontSize: 30, fontWeight: 900, color: 'black' }}
              >
                Welcome
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#aeaeae' }} gutterBottom>
                Choose your preferred sign-in method
              </Typography>
            </div>
            <div
              style={{
                marginTop: 25,
                display: 'flex',
                gap: 15,
                flexDirection: 'column',
                borderBottom: '1px solid #aeaeae',
                paddingBottom: 25,
              }}
            >
              <Button
                variant='outlined'
                onClick={() =>
                  (window.location.href = `/.auth/login/aad?post_login_redirect_uri=${window.location.origin}`)
                }
                className={classes.button}
              >
                <img
                  src={MICROSOFT_LOGO_PATH}
                  alt='Microsoft'
                  style={{ height: 24 }}
                />
                <span style={{ margin: '3px 0px 0px 10px' }}>
                  Sign in with Microsoft
                </span>
              </Button>
              <Button
                variant='outlined'
                onClick={() =>
                  (window.location.href = `/.auth/login/google?post_login_redirect_uri=${window.location.origin}`)
                }
                className={classes.button}
              >
                <img
                  src={GOOGLE_LOGO_PATH}
                  alt='Google'
                  style={{ height: 24 }}
                />
                <span style={{ margin: '3px 0px 0px 10px' }}>
                  Sign in with Google
                </span>
              </Button>
            </div>
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <span style={{ fontSize: 14, color: '#aeaeae', marginRight: 4 }}>
                Don't have registered account?
              </span>
              <a
                target='_blank'
                rel='noreferrer'
                style={{ fontSize: 16, fontWeight: 600 }}
                href={authSettings?.registrationFormUrl}
              >
                Sign up here
              </a>
            </div>

            <div className={classes.securityNotice}>
              <h4>
                <span role='img' aria-label='Locked padlock'>
                  🔐
                </span>{' '}
                Website Access Notice – Acceptable Use & Security Policy
              </h4>
              <p>
                By logging into this website, you acknowledge and agree to the
                following:
              </p>

              <p>
                <strong>
                  <span role='img' aria-label='Check mark'>
                    ✅
                  </span>{' '}
                  Acceptable Use
                </strong>
              </p>
              <ul>
                <li>
                  Access is restricted to <strong>authorized users</strong> for{' '}
                  <strong>business purposes only</strong>.
                </li>
                <li>
                  You must comply with the company's{' '}
                  <strong>Acceptable Use Policy</strong>, which prohibits:
                  <ul>
                    <li>Unauthorized access or sharing of credentials.</li>
                    <li>
                      Distribution of confidential or sensitive information.
                    </li>
                    <li>
                      Use of the system for personal, illegal, or non-Whitehaven
                      business-related activities.
                    </li>
                  </ul>
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='Locked padlock'>
                    🔒
                  </span>{' '}
                  Security Protocols
                </strong>
              </p>
              <ul>
                <li>
                  This website uses <strong>secure encryption</strong> to
                  protect your session.
                </li>
                <li>
                  You are responsible for maintaining the{' '}
                  <strong>confidentiality of your login credentials</strong>.
                </li>
                <li>
                  Any attempt to <strong>bypass security controls</strong> or
                  access unauthorized data is strictly prohibited and may result
                  in the removal of access.
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='Warning sign'>
                    ⚠️
                  </span>{' '}
                  Monitoring & Compliance
                </strong>
              </p>
              <ul>
                <li>
                  All activity on this system is <strong>logged</strong> for
                  security and compliance.
                </li>
                <li>
                  <strong>Suspicious behaviour</strong> may trigger an
                  investigation and be reported to Whitehaven Cyber Security.
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='No entry sign'>
                    🚫
                  </span>{' '}
                  Consequences of Misuse
                </strong>
              </p>
              <ul>
                <li>
                  Violations of these policies may result in{' '}
                  <strong>disciplinary action</strong>, including termination.
                </li>
                <li>
                  <strong>Unauthorized access</strong> or misuse may lead to{' '}
                  <strong>legal consequences</strong> and financial liability.
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
